import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { Sparkles, X } from 'lucide-react';
import PromoCouponChips from './PromoCouponChips';
import { auth, db } from '../firebase';
import { isPaidTier } from '../constants/roles';
import {
  PUBLIC_SITE_CONTENT_COLLECTION,
  PROMO_BANNER_DOC_ID,
  PROMO_BANNER_DISMISS_KEY,
  VARIANT_STYLES,
  resolvePromoBanner,
} from '../constants/promoBanner';

function readDismissedVersion() {
  try {
    const raw = window.localStorage.getItem(PROMO_BANNER_DISMISS_KEY);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeDismissedVersion(version) {
  try {
    window.localStorage.setItem(PROMO_BANNER_DISMISS_KEY, String(version));
  } catch {
    // localStorage can throw in private mode / disabled storage. Silent fail
    // is fine — the user just sees the banner again next page load.
  }
}

/**
 * Top-of-page promotional banner. Subscribes to publicSiteContent/promoBanner
 * and renders only when `enabled === true` and the user has not already
 * dismissed the current banner version.
 *
 * Bumping the version in the admin editor effectively un-dismisses the
 * banner for everyone, which is what we want when the offer changes.
 *
 * Mount this once near the top of any page tree (e.g. inside Layout, and
 * inside the public Login page).
 */
export default function PromoBanner() {
  const [banner, setBanner] = useState(null);
  const [dismissedVersion, setDismissedVersion] = useState(() => readDismissedVersion());
  // Auth + subscription gating: the default promo banner is a subscribe/coupon
  // CTA, so it's only relevant to signed-in users who don't already have a
  // paid subscription. `uid === null` => logged out (no banner); `isPaid`
  // => paid subscriber (no banner). Admins/override/cancelling are treated as
  // paid by isPaidTier, so they don't see the upsell either.
  const [uid, setUid] = useState(() => auth.currentUser?.uid || null);
  const [isPaid, setIsPaid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => setUid(u?.uid || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!uid) {
      setIsPaid(false);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, 'users', uid),
      (snap) => setIsPaid(snap.exists() ? isPaidTier(snap.data()) : false),
      (err) => {
        // Be conservative on read error: treat as paid so we never risk
        // flashing a subscribe upsell at an existing paying customer.
        console.warn('[PromoBanner] user doc snapshot error:', err?.message || err);
        setIsPaid(true);
      }
    );
    return () => unsub();
  }, [uid]);

  useEffect(() => {
    const ref = doc(db, PUBLIC_SITE_CONTENT_COLLECTION, PROMO_BANNER_DOC_ID);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setBanner(resolvePromoBanner(data));
      },
      (err) => {
        // Public read is allowed by the rules, but be defensive: keep the
        // banner hidden on any read error rather than break the page.
        console.warn('[PromoBanner] snapshot error:', err?.message || err);
        setBanner(null);
      }
    );
    return () => unsub();
  }, []);

  const visible = useMemo(() => {
    // Logged-in users only.
    if (!uid) return false;
    // Don't upsell a subscription to someone who already pays for one.
    if (isPaid) return false;
    if (!banner) return false;
    if (!banner.enabled) return false;
    if (banner.version <= dismissedVersion) return false;
    return true;
  }, [uid, isPaid, banner, dismissedVersion]);

  if (!visible) return null;

  const styles = VARIANT_STYLES[banner.variant] || VARIANT_STYLES.teal;

  const handleDismiss = () => {
    writeDismissedVersion(banner.version);
    setDismissedVersion(banner.version);
  };

  const handleCta = () => {
    const url = banner.ctaUrl || '/checkout';
    if (/^https?:\/\//i.test(url)) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  };

  return (
    <div
      role="region"
      aria-label="Promotional offer"
      className={`relative w-full ${styles.bar} shadow-sm`}
    >
      <div className="flex items-center gap-3 px-3 sm:px-5 py-2 sm:py-2.5 max-w-screen-2xl mx-auto">
        <Sparkles size={16} className="shrink-0 hidden sm:block" />
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:text-sm">
          <span className="font-medium leading-snug">{banner.message}</span>
          <PromoCouponChips codes={banner.couponCodes} chipClassName={styles.chip} />
        </div>
        <button
          type="button"
          onClick={handleCta}
          className={`shrink-0 rounded-md px-3 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${styles.cta}`}
        >
          {banner.ctaLabel}
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss promotional banner"
          className={`shrink-0 p-1 rounded transition-colors ${styles.dismiss}`}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
